import { addDays } from "date-fns";
import { useEffect } from "react";

// Valid time options matching the Calendar's TimeSelector
const VALID_HOURS = [0, 9, 12, 17];

/**
 * Snap an hour to the closest valid time option.
 * Valid options: 0 (Midnight), 9 (9am), 12 (Noon), 17 (5pm)
 */
const snapToValidHour = (hour: number): number => {
	return VALID_HOURS.reduce((closest, valid) => {
		const currentDiff = Math.abs(hour - closest);
		const newDiff = Math.abs(hour - valid);
		return newDiff < currentDiff ? valid : closest;
	}, VALID_HOURS[0]);
};
/**
 * Normalize a date to use valid time options.
 * - Converts 11:59 PM to midnight of next day (for end dates)
 * - Snaps other invalid hours to closest valid option
 */
export const normalizeDateTime = (date: Date, isEndDate: boolean = false): Date => {
	const normalized = new Date(date);

	// Special handling for end-of-day times (legacy endOfDay usage)
	if (isEndDate && normalized.getHours() === 23) {
		const nextDay = addDays(normalized, 1);
		nextDay.setHours(0, 0, 0, 0);
		return nextDay;
	}

	// Snap to closest valid hour
	const currentHour = normalized.getHours();
	const validHour = snapToValidHour(currentHour);
	normalized.setHours(validHour, 0, 0, 0);

	return normalized;
};

/**
 * Check if a date needs normalization (has an invalid hour or non-zero minutes/seconds)
 */
const needsNormalization = (date: Date | null | undefined): boolean => {
	if (!date) return false;
	const hour = date.getHours();
	const minutes = date.getMinutes();
	const seconds = date.getSeconds();
	const milliseconds = date.getMilliseconds();

	// Needs normalization if hour is invalid OR if minutes/seconds/ms are non-zero
	return !VALID_HOURS.includes(hour) || minutes !== 0 || seconds !== 0 || milliseconds !== 0;
};

/**
 * Component that watches form date values and normalizes them when they have invalid times.
 */
export const DateNormalizationEffect = <
	T extends { startDate: Date | null | undefined; endDate: Date | null | undefined },
>({
	startDate,
	endDate,
	setFieldValue,
}: {
	startDate: Date | null | undefined;
	endDate: Date | null | undefined;
	setFieldValue: <K extends keyof T>(name: K, value: T[K]) => void;
}) => {
	useEffect(() => {
		if (startDate && needsNormalization(startDate)) {
			const normalized = normalizeDateTime(startDate, false);
			setFieldValue("startDate", normalized);
		}
	}, [startDate, setFieldValue]);

	useEffect(() => {
		if (endDate && needsNormalization(endDate)) {
			const normalized = normalizeDateTime(endDate, true);
			setFieldValue("endDate", normalized);
		}
	}, [endDate, setFieldValue]);

	return null;
};
